import { type Key } from 'react-aria';
import { useState } from 'react';
import { Select } from '../../base/select/select';
import { isValidTimezone } from '@goodneighbor/utilities/time/index';

const timezones = Intl.supportedValuesOf('timeZone');
const supportedTimezones = ['America', 'Europe']
  .map((region) => timezones.filter((tz) => tz.startsWith(region + '/')))
  .flat();

export type TimezoneSelectProps = {
  label?: string;
  labelClassName?: string;
  isRequired?: boolean;
  defaultValue?: string;
  onChange: (value: Key | null) => void;
  tooltip?: string;
};

export function TimezoneSelect({
  label,
  labelClassName,
  onChange,
  isRequired,
  defaultValue,
  tooltip
}: TimezoneSelectProps) {
  const [defaultSelectedKey] = useState(() => {
    const hasValidDefaultTimezone = isValidTimezone(defaultValue || '');
    const defaultTimezone = hasValidDefaultTimezone
      ? Intl.DateTimeFormat(undefined, {
          timeZone: defaultValue
        }).resolvedOptions().timeZone
      : Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!hasValidDefaultTimezone) {
      onChange(defaultTimezone); // Info: on initial render set default value to the form
    }
    return defaultTimezone;
  });
  const items = supportedTimezones.map((tz) => ({
    label: `${tz.replaceAll('_', ' ')} (${new Date().toLocaleString('en-US', { timeZone: tz, timeZoneName: 'short' }).split(', ').pop()?.split(' ').pop()})`,
    supportingText: new Date().toLocaleString('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit'
    }),
    id: tz
  }));

  return (
    <Select.ComboBox
      items={items}
      shortcut={false}
      placeholder="Select Timezone"
      defaultSelectedKey={defaultSelectedKey}
      onSelectionChange={onChange}
      label={label}
      labelClassName={labelClassName}
      isRequired={isRequired}
      tooltip={tooltip}
    >
      {(item) => (
        <Select.Item
          key={item.id}
          id={item.id}
          supportingText={item.supportingText}
        >
          {item.label}
        </Select.Item>
      )}
    </Select.ComboBox>
  );
}
